/**
 * IT Admin → "Who should this go to?" (16 Oct): the dispatcher's question.
 *
 * Enter a ticket number, or paste the ticket's text (often Ivanti's
 * "Summarize"). The answer comes from the routing register first, then
 * history, then the AI reading the ticket against the register, with a
 * confidence and the reasons. Unsure answers ask for a decision, which is
 * saved for next time; a ticket can be reassigned here with a reason.
 */

import { Button, Field, Textarea, Typography } from "@norican/react";
import { type FormEvent, useState } from "react";

import { ErrorState, SectionCard } from "@/components/common";
import { useT } from "@/i18n";

import { useSuggestAssignment } from "./api";
import { DecisionPanel } from "./decision-panel";
import { parseQuery } from "./lib";
import { ReassignCard } from "./reassign-card";
import { SuggestionView } from "./suggestion-view";

export function AssignPage() {
  const { t } = useT("tickets");
  const [input, setInput] = useState("");
  const suggest = useSuggestAssignment();
  const query = parseQuery(input);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (query === null) {
      return;
    }
    suggest.mutate("ticketNumber" in query ? { ticket_number: query.ticketNumber } : query);
  }

  const answer = suggest.data;
  return (
    <div className="flex flex-col gap-6">
      <SectionCard title={t("itAdmin.section.assign")}>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Typography variant="body-sm" tone="muted">
            {t("itAdmin.assign.intro")}
          </Typography>
          <Field name="assign-query" label={t("itAdmin.assign.inputLabel")}>
            <Textarea
              name="assign-query"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t("itAdmin.assign.inputPlaceholder")}
              rows={2}
              autoResize
              maxLength={8000}
            />
          </Field>
          <div>
            <Button type="submit" loading={suggest.isPending} disabled={query === null}>
              {t("itAdmin.assign.ask")}
            </Button>
          </div>
        </form>
      </SectionCard>
      {suggest.error == null ? null : <ErrorState error={suggest.error} variant="inline" />}
      {answer ? (
        <div
          key={`${answer.ticket?.ticket_id ?? "text"}-${String(suggest.submittedAt)}`}
          className="flex flex-col gap-6"
        >
          <SuggestionView answer={answer} />
          {answer.needs_decision ? <DecisionPanel answer={answer} /> : null}
          {answer.ticket && answer.can_reassign ? <ReassignCard ticket={answer.ticket} /> : null}
        </div>
      ) : null}
    </div>
  );
}
